/**
 * @fileoverview Manual Wallet Modal Component
 * Modal for manually entering Zcash addresses for read-only wallet connection
 */

import React, { useState } from 'react';
import { X, Copy, Eye, AlertCircle, Check } from 'lucide-react';

interface ManualWalletModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConnect: (shieldedAddress: string, transparentAddress: string) => void;
}

const ManualWalletModal: React.FC<ManualWalletModalProps> = ({ 
	isOpen, 
	onClose, 
	onConnect 
}) => {
	const [shieldedAddress, setShieldedAddress] = useState('');
	const [transparentAddress, setTransparentAddress] = useState('');
	const [errors, setErrors] = useState<{ shielded?: string; transparent?: string }>({});
	const [isConnecting, setIsConnecting] = useState(false);

	// Validate Zcash addresses
	const validateAddresses = () => {
		const newErrors: { shielded?: string; transparent?: string } = {};

		if (!shieldedAddress.trim()) {
			newErrors.shielded = 'Shielded address is required';
		} else if (!isValidShieldedAddress(shieldedAddress)) {
			newErrors.shielded = 'Invalid shielded address format (should start with zs1)';
		}

		if (transparentAddress.trim() && !isValidTransparentAddress(transparentAddress)) {
			newErrors.transparent = 'Invalid transparent address format (should start with t1)';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Basic address validation
	const isValidShieldedAddress = (address: string): boolean => {
		return address.startsWith('zs1') && address.length === 78;
	};

	const isValidTransparentAddress = (address: string): boolean => {
		return address.startsWith('t1') && address.length === 35;
	};

	// Handle form submission
	const handleConnect = async () => {
		if (!validateAddresses()) return;

		setIsConnecting(true);
		try {
			await onConnect(shieldedAddress.trim(), transparentAddress.trim() || '');
			onClose();
		} catch (error) {
			console.error('Manual connection failed:', error);
		} finally {
			setIsConnecting(false);
		}
	};

	// Reset form when modal closes
	const handleClose = () => {
		setShieldedAddress('');
		setTransparentAddress('');
		setErrors({});
		setIsConnecting(false);
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* Backdrop */}
			<div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
			
			{/* Modal */}
			<div className="flex items-center justify-center min-h-screen p-4">
				<div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full">
					{/* Header */}
					<div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-xl">
						<div className="flex items-center justify-between">
							<h2 className="text-xl font-bold text-white">Manual Wallet Setup</h2>
							<button
								onClick={handleClose}
								className="text-white hover:text-gray-200 transition-colors"
								aria-label="Close modal"
							>
								<X className="w-6 h-6" />
							</button>
						</div>
						<p className="text-blue-100 text-sm mt-2">
							Enter your Zcash addresses for read-only access
						</p>
					</div>

					{/* Content */}
					<div className="p-6">
						{/* Information Banner */}
						<div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<Eye className="w-5 h-5 text-blue-600 mt-0.5" />
								<div>
									<h4 className="text-sm font-medium text-blue-900">Read-Only Mode</h4>
									<p className="text-sm text-blue-700 mt-1">
										This will connect your addresses in read-only mode. You can browse and view NFPTs, 
										but for transactions you'll need to manually copy the details to your Zcash wallet.
									</p>
								</div>
							</div>
						</div>

						{/* Form */}
						<div className="space-y-4">
							{/* Shielded Address */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Shielded Address (Required)
								</label>
								<input
									type="text"
									value={shieldedAddress}
									onChange={(e) => setShieldedAddress(e.target.value)}
									placeholder="zs1..."
									className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
										errors.shielded 
											? 'border-red-300 focus:ring-red-500' 
											: 'border-gray-300'
									}`}
								/>
								{errors.shielded && (
									<p className="mt-1 text-sm text-red-600 flex items-center">
										<AlertCircle className="w-4 h-4 mr-1" />
										{errors.shielded}
									</p>
								)}
								<p className="mt-1 text-xs text-gray-500">
									Your shielded address (starts with zs1). This is required for NFPT operations.
								</p>
							</div>

							{/* Transparent Address */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Transparent Address (Optional)
								</label>
								<input
									type="text"
									value={transparentAddress}
									onChange={(e) => setTransparentAddress(e.target.value)}
									placeholder="t1... (optional)"
									className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
										errors.transparent 
											? 'border-red-300 focus:ring-red-500' 
											: 'border-gray-300'
									}`}
								/>
								{errors.transparent && (
									<p className="mt-1 text-sm text-red-600 flex items-center">
										<AlertCircle className="w-4 h-4 mr-1" />
										{errors.transparent}
									</p>
								)}
								<p className="mt-1 text-xs text-gray-500">
									Your transparent address (starts with t1). Optional, but useful for some operations.
								</p>
							</div>
						</div>

						{/* Privacy Notice */}
						<div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
								<div>
									<h4 className="text-sm font-medium text-yellow-900">Privacy Notice</h4>
									<p className="text-sm text-yellow-700 mt-1">
										Your addresses are only stored locally in your browser. They're not sent to any servers 
										except for balance and transaction queries to the Zcash network.
									</p>
								</div>
							</div>
						</div>

						{/* Action Buttons */}
						<div className="mt-6 flex space-x-3">
							<button
								onClick={handleClose}
								className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleConnect}
								disabled={isConnecting || !shieldedAddress.trim()}
								className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
							>
								{isConnecting ? (
									<>
										<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
										Connecting...
									</>
								) : (
									<>
										<Check className="w-4 h-4 mr-2" />
										Connect Wallet
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ManualWalletModal;